import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { StyledTrash, StyledContent } from '../../../../common/components/actions/trashBtn'
import globalConfirmation from 'live/services/global-confirmation'
import { DuplicateButton } from '../../../../common/components/actions/duplicateBtn'
import {
    DuplicateForm,
    Edit,
    ManulaSendReportActionIcon,
    StyledSendManualReport
} from '../../../../common/components/actions/edit'
import IPublishButton from './components/InlinePublishButton'
import moment from 'moment'
import {
    Item,
    CardStyled,
    ReportName,
    ReportStatus,
    Author,
    EditStatus,
    LastExecution,
    Actions,
    TrashIconWithText
} from './styles'
import { sleep } from '../../../../api/helper'
import type Report from '../../../../common/types/api/report'
import i18n from 'live/services/i18n'
import getReport from '../../../../api/getReport'
import sendManualReport from '../../../../api/sendManualReport'
import LiveModal from 'live/components/ui/Modal'
import ComponentManualReportSend, { ManualReportSendData } from '../../ComponentManualReportSend'
import { Recipient } from '../../../../common/types/form'
import DropDownIcon from '../../../../common/components/options/DropDownIcon'
import { filterAllowedReportLink } from '../../reportLinks'

interface Actions {
    published: boolean
}
interface Props {
    report: Report
    actionDelete: (uid: string) => void
    actionPublish: (uid: string) => void
    actionDuplicate: (uid: string, newTitle: string) => void
}

export default function CardList({ report, actionDelete, actionPublish, actionDuplicate }: Props): JSX.Element {
    const [loading, setLoading] = useState<boolean>(false)

    const [manualReportShowModal, setmanualReportShowModal] = useState<boolean>(false)
    const defaultManualReportSendData = {
        timespan: null,
        selectedRecipientGroups: [] as Recipient[],
        reportSource: report
    } as ManualReportSendData

    const [manualReportSendData, setManualReportSendData] = useState<ManualReportSendData>(defaultManualReportSendData)

    const [duplicateReportTitle, setDuplicateReportTitle] = useState<string>('')

    const resetManualReportModalData = (): void | null => {
        setmanualReportShowModal(false)
        setManualReportSendData(defaultManualReportSendData)
    }

    useEffect(() => {
        setDuplicateReportTitle(`${report?.title ?? ''} (copy)`)
    }, [report?.title])

    const history = useHistory()

    const formatDate = (date: number): string => {
        if(date == 0 || isNaN(date) || date === undefined)
            return '-'
        
        const stringDate = moment(date || '').format('DD/MM/YYYY | HH:mm:ss A').replace("|", "at")
        //TODO check why i18n is not working to translate at the comparation
        if (stringDate.trim() == i18n('Invalid date').trim()) return '-'

        return stringDate
    }

    const FIRST_REPORT_LINK = filterAllowedReportLink()[0]?.link
 
    return (
        <>
            <CardStyled key={report.uid} onClick={() => history.push(`${FIRST_REPORT_LINK}/${report.uid}`)}>
                <ReportName> {report.title}</ReportName>
                <Author> {report?.createdBy?.name || i18n('desconhecido')}</Author>
                <LastExecution>{formatDate(report.lastExecutedAt)}</LastExecution>
                <ReportStatus enabled={report.enabled}>
                    <span className="report_status_span">{report.enabled ? 'Active' : 'Inactive'} </span>
                </ReportStatus>
                <EditStatus
                    onClick={ev => {
                        ev.stopPropagation()
                    }}
                >
                    <IPublishButton
                        label={loading ? 'Publishing...' : report.isDraft ? 'Publish' : 'Published'}
                        onClick={async () => {
                            setLoading(true)
                            actionPublish(report.uid)
                            sleep(1200).then(async () => {
                                const checkPublish = await getReport(report.uid)
                                if (!checkPublish.isDraft) {
                                    setLoading(false)
                                } else {
                                    sleep(1000).then(() => setLoading(false))
                                }
                            })
                        }}
                        disabled={!report.isDraft}
                        loading={loading}
                        status={
                            report.isDraft ? 'unpublished' : loading && !report.isDraft ? 'unpublished' : 'published'
                        }
                    />
                </EditStatus>
                <Actions>
                    <Item>
                        <Edit
                            className="far fa-edit"                            
                        />
                    </Item>
                    <Actions
                        onClick={ev => {
                            ev.stopPropagation()
                        }}
                    >
                        <DropDownIcon title={'report options'}>
                            {report.isDraft ? null : (
                                <StyledSendManualReport onClick={() => setmanualReportShowModal(true)}>
                                    <ManulaSendReportActionIcon className="far fa-paper-plane" />
                                    <span>{i18n('Send Manual Report')}</span>
                                </StyledSendManualReport>
                            )}
                            <DuplicateButton
                                title={i18n("Duplicate Report")}
                                onConfirm={() => actionDuplicate(report.uid, duplicateReportTitle)}
                            >
                                <DuplicateForm>
                                    <label htmlFor="title">Report Title</label>
                                    <input
                                        type="text"
                                        value={duplicateReportTitle}
                                        onChange={({ target }) => {
                                            setDuplicateReportTitle(target.value)
                                        }}
                                    />
                                </DuplicateForm>
                            </DuplicateButton>
                            <TrashIconWithText
                                onClick={() =>
                                    globalConfirmation.confirm(
                                        <StyledContent>
                                            {i18n(`
                                            Are you sure you want to remove report ${report.title}?
                                            `)}
                                            <br />
                                            <strong className="warning">
                                                {i18n('This action can not be undone.')}
                                            </strong>
                                        </StyledContent>,
                                        () => actionDelete(report.uid)
                                    )
                                }
                            >
                                <StyledTrash className="far fa-trash-alt" />
                                <span>{i18n('Delete Report')}</span>
                            </TrashIconWithText>
                        </DropDownIcon>
                    </Actions>
                </Actions>
            </CardStyled>

            <LiveModal
                show={manualReportShowModal}
                onHide={() => resetManualReportModalData()}
                animation={false}
                className="modal-lg"
            >
                <LiveModal.Header>
                    <LiveModal.Title>{i18n('Send manual report')}</LiveModal.Title>
                </LiveModal.Header>
                <LiveModal.Body>
                    <ComponentManualReportSend
                        manualReportSendData={manualReportSendData}
                        onChange={setManualReportSendData}
                    />
                </LiveModal.Body>
                <LiveModal.Footer>
                    <LiveModal.ModalActions
                        onSubmit={() =>
                            sendManualReport(
                                report.uid,
                                null, // TODO: implement reference date in back-end
                                manualReportSendData.timespan,
                                manualReportSendData.selectedRecipientGroups
                            ).finally(() => {
                                resetManualReportModalData()
                            })
                        }
                        submitText={i18n('Send')}
                        submitBtnClassName="btn btn-primary"
                        onCancel={() => resetManualReportModalData()}
                        cancelText={i18n('Cancel')}
                    />
                </LiveModal.Footer>
            </LiveModal>
        </>
    )
}
